import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "mt-1.5 bg-obsidian border-gold/25 text-parchment focus-visible:ring-gold rounded-none";

export default function ResetPassword() {
  const { token } = useParams();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) {
      setError("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      setDone(true);
      toast.success("Password set. Welcome back to the order.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="relative overflow-hidden pt-16 min-h-screen flex items-center justify-center noise-overlay crosshair-grid px-5" data-testid="reset-password-page">
      <div className="w-full max-w-md border border-gold/25 bg-card2/60 backdrop-blur p-8 sm:p-10 my-16">
        <div className="flex flex-col items-center text-center">
          <LogoMark className="w-14 h-14 text-gold" />
          <h1 className="font-display text-2xl uppercase tracking-wider text-parchment mt-5">Set New Password</h1>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone-500 mt-2">One-time login link</p>
        </div>

        {done ? (
          <div className="mt-8 text-center" data-testid="reset-success">
            <p className="text-sm text-stone-300 leading-relaxed">Your password is set. Use it to sign in to the Members HQ.</p>
            <Link
              to="/login"
              data-testid="reset-goto-login"
              className="mt-6 inline-block gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.22em] font-semibold px-8 py-3.5 hover:bg-gold-light transition-colors"
            >
              Go to Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">New Password</Label>
              <Input data-testid="reset-password-input" required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
            </div>
            <div>
              <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Confirm Password</Label>
              <Input data-testid="reset-confirm-input" required type="password" minLength={6} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} />
            </div>

            {error && (
              <p className="border border-crimson/50 bg-crimson/10 text-crimson text-sm px-4 py-3" data-testid="reset-error">{error}</p>
            )}

            <button
              type="submit"
              data-testid="reset-submit-button"
              disabled={busy}
              className="w-full gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.22em] font-semibold py-3.5 hover:bg-gold-light hover:shadow-[0_0_24px_rgba(212,175,55,0.35)] transition-[background-color,box-shadow] duration-300 disabled:opacity-60"
            >
              {busy ? "One moment..." : "Set Password"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
